// src/components/FlirtCenter.js
import React, { useEffect, useState, useMemo, useRef } from "react";
import apiClient from "../api/client";
import { Sparkles, Gift, HeartHandshake, Filter, Loader2, ArrowLeft } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ProfileViewModal } from "./profile/ProfileViewModal";
import { useProfileViewer } from "../hooks/useProfileViewer";
import { getAgeFromDob } from "../utils/ageCalculator";
import { resolveProfilePhotoUrl } from "../utils/profileMedia";

const API = window.location.origin;

// FIX #5: Deduplicate photo array to prevent carousel glitch
const deduplicatePhotos = (photos) => {
  if (!Array.isArray(photos)) return [];
  const seen = new Set();
  return photos.filter((photo) => {
    if (!photo) return false;
    const url = typeof photo === 'string' ? photo : photo.url;
    if (!url || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
};

const FILTERS = [
  { label: "All", value: "all" },
  { label: "Flirts", value: "flirt" },
  { label: "Gifts", value: "gift" },
];

// Patch 1: Session storage key for preserving scroll/index position
const FLIRTS_UI_STATE_KEY = "unitylove_flirts_ui_state_v1";

function FlirtCenter() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // "all" | "flirt" | "gift"
  
  // P2: Profile viewer modal
  const profileViewer = useProfileViewer();

  // 🔧 FIX: Mark flirts as seen when viewing this page
  // Guard against double-clear in StrictMode
  const didClearRef = useRef(false);
  
  // Patch 1: Restore scroll position on mount
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(FLIRTS_UI_STATE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);

      // Restore scroll after DOM paints
      requestAnimationFrame(() => {
        if (typeof parsed.scrollY === "number") {
          window.scrollTo(0, parsed.scrollY);
        }
      });
    } catch {
      // ignore
    }
  }, []);

  // Patch 1: Persist scroll position on scroll
  useEffect(() => {
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        try {
          const raw = sessionStorage.getItem(FLIRTS_UI_STATE_KEY);
          const prev = raw ? JSON.parse(raw) : {};
          sessionStorage.setItem(
            FLIRTS_UI_STATE_KEY,
            JSON.stringify({
              ...prev,
              scrollY: window.scrollY,
            })
          );
        } catch {
          // ignore
        }
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  
  useEffect(() => {
    const markFlirtsSeen = async () => {
      if (!token || didClearRef.current) return;
      didClearRef.current = true;
      
      try {
        const response = await apiClient.post(`/notifications/clear`,
          { type: 'flirts' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Use returned summary immediately (faster than re-fetch)
        if (response.data?.summary) {
          window.updateBadgeCounts?.(response.data.summary);
          window.updateDashboardBadges?.(response.data.summary);
        } else {
          window.refreshNotificationSummary?.();
        }
      } catch (err) {
        console.error('[FlirtCenter] Failed to mark flirts as seen:', err);
      }
    };
    
    markFlirtsSeen();
  }, [token]);

  const fetchActivity = async () => {
    setLoading(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const merged = [];
      const seenKeys = new Set();

      // 1) Try unified /activity/received endpoint first
      try {
        const res = await apiClient.get(`/activity/received`, { headers });
        const data = Array.isArray(res.data) ? res.data : [];

        data.forEach((item) => {
          const id = item.id || item._id || `${item.type}-${item.created_at}`;
          const type = item.type || (item.flirt_type ? "flirt" : "unknown");
          const key = `${type}:${id}`;

          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            merged.push({
              ...item,
              type, // make sure type is set
              id,
            });
          }
        });
        console.log('[FlirtCenter] Loaded', data.length, 'activities from /activity/received');
      } catch (err) {
        console.error("[FlirtCenter] /api/activity/received failed, will fall back:", err);
      }

      // 2) Always also fetch /gifts/received and merge in
      try {
        const resGifts = await apiClient.get(`/gifts/received`, { headers });
        const gifts = Array.isArray(resGifts.data) ? resGifts.data : [];

        gifts.forEach((g) => {
          const id = g.id || g._id || g.transaction_id;
          const key = `gift:${id}`;

          if (!seenKeys.has(key)) {
            seenKeys.add(key);

            merged.push({
              type: "gift",
              id,
              created_at: g.created_at,
              from_user_id:
                g.from_user_id || g.sender_id || g.sender?.id || g.metadata?.from_user_id,
              from_user_name:
                g.sender_profile?.names?.join(" & ") ||
                g.sender_name ||
                g.metadata?.from_user_name ||
                "Someone",
              gift_key: g.gift_key || g.metadata?.gift_key,
              gift_name: g.gift_name || g.metadata?.gift_name,
              gift_emoji: g.gift_emoji || g.metadata?.gift_emoji,
              gift_cost: g.cost || g.amount || g.metadata?.gift_cost,
              sender_profile: g.sender_profile,
            });
          }
        });
        console.log('[FlirtCenter] Loaded', gifts.length, 'gifts from /gifts/received');
      } catch (err) {
        console.error("[FlirtCenter] /api/gifts/received failed:", err);
      }

      // 3) Sort newest → oldest
      merged.sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      );

      setActivities(merged);
      console.log('[FlirtCenter] Total merged activities:', merged.length);
      
      // Clear flirts notification badge
      try {
        await apiClient.post(`/notifications/clear`, 
          { type: 'flirts' },
          { headers }
        );
      } catch (err) {
        console.error('[FlirtCenter] Failed to clear notification', err);
      }
    } catch (err) {
      console.error("[FlirtCenter] Failed to load activity:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, [token]);

  // When tab regains focus, refresh activity so you don't need full reload
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        fetchActivity();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  const filteredActivities = useMemo(() => {
    if (filter === "all") return activities;
    return activities.filter((a) => a.type === filter);
  }, [activities, filter]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-50">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="inline-flex items-center text-xs sm:text-sm px-3 py-1 rounded-full bg-pink-500/10 hover:bg-pink-500/20 text-pink-200 border border-pink-500/30 transition"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span>Back to Dashboard</span>
              </button>

              <div className="hidden sm:flex items-center gap-2">
                <button
                  type="button"
                  onClick={fetchActivity}
                  className="rounded-full bg-slate-900 px-3 py-1 text-[11px] font-medium text-slate-200 hover:bg-slate-800 transition"
                >
                  Refresh
                </button>
                <span className="inline-flex items-center gap-2 rounded-full bg-pink-500/10 px-3 py-1 text-xs font-medium text-pink-300 border border-pink-500/30">
                  <Sparkles className="w-3 h-3" />
                  Flirt Center
                </span>
              </div>
            </div>

            <div>
              <span className="sm:hidden inline-flex items-center gap-2 rounded-full bg-pink-500/10 px-3 py-1 text-xs font-medium text-pink-300 border border-pink-500/30">
                <Sparkles className="w-3 h-3" />
                Flirt Center
              </span>
              <h1 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight">
                Your Recent Interactions
              </h1>
              <p className="mt-1 text-sm text-slate-400 max-w-md">
                See who's flirting with you and who's sending you gifts — all in one place.
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center text-xs text-slate-400">
              <Filter className="w-3 h-3 mr-1" /> Filter
            </span>
            <div className="inline-flex rounded-full bg-slate-900/70 p-1 border border-slate-700/70">
              {FILTERS.map((f) => {
                const isActive = filter === f.value;
                return (
                  <button
                    key={f.value}
                    onClick={() => setFilter(f.value)}
                    className={
                      "px-3 py-1 text-xs rounded-full transition " +
                      (isActive
                        ? "bg-pink-500 text-white shadow-sm"
                        : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/80")
                    }
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
        </header>

        {/* Stats / Summary */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-2xl bg-slate-900/80 border border-slate-700/70 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Total Interactions</span>
              <HeartHandshake className="w-4 h-4 text-pink-400" />
            </div>
            <div className="mt-2 text-xl font-semibold">{activities.length}</div>
          </div>
          <div className="rounded-2xl bg-slate-900/80 border border-slate-700/70 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Flirts</span>
              <Sparkles className="w-4 h-4 text-amber-300" />
            </div>
            <div className="mt-2 text-xl font-semibold">
              {activities.filter((a) => a.type === "flirt").length}
            </div>
          </div>
          <div className="rounded-2xl bg-slate-900/80 border border-slate-700/70 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Gifts</span>
              <Gift className="w-4 h-4 text-emerald-300" />
            </div>
            <div className="mt-2 text-xl font-semibold">
              {activities.filter((a) => a.type === "gift").length}
            </div>
          </div>
        </section>

        {/* Activity List */}
        <section className="mt-2">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading your recent activity…
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center text-slate-400 text-sm gap-2">
              <Sparkles className="w-6 h-6 text-slate-500" />
              <p>No activity yet.</p>
              <p className="text-xs text-slate-500">
                When someone flirts with you or sends you a gift, it will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredActivities.map((item) => (
                <ActivityItem key={`${item.type}-${item.id}`} activity={item} profileViewer={profileViewer} />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* P2: Profile View Modal */}
      <ProfileViewModal
        open={profileViewer.open}
        onClose={profileViewer.close}
        profile={profileViewer.profile}
        isSelf={profileViewer.isSelf}
        source={profileViewer.source}
        onLikeFromProfile={profileViewer.onLikeFromProfile}
        onPassFromProfile={profileViewer.onPassFromProfile}
      />
    </div>
  );
}

function ActivityItem({ activity, profileViewer }) {
  const isFlirt = activity.type === "flirt";
  const isGift = activity.type === "gift";

  const createdAtLabel = activity.created_at
    ? formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })
    : "";

  const name = activity.from_user_name || "Someone";

  const flirtLabel = (flirtType) => {
    switch (flirtType) {
      case "wink":
        return "winked at you 😉";
      case "wave":
        return "waved at you 👋";
      case "heart_eyes":
        return "sent you heart eyes 😍";
      case "fire":
        return "sent you some 🔥 energy";
      default:
        return "sent you a flirt 💫";
    }
  };

  // Get sender profile photo using canonical resolver
  const profile = activity.other_user_profile || activity.sender_profile || activity.profile || null;
  const profilePhoto = resolveProfilePhotoUrl(profile);

  const renderLeftIcon = () => {
    // Show profile photo if available
    if (profilePhoto) {
      return (
        <button 
          onClick={handleShowProfile}
          className="relative flex-shrink-0"
        >
          <img 
            src={profilePhoto} 
            alt={name}
            className="h-12 w-12 rounded-full object-cover border-2 border-slate-700 hover:border-pink-500/60 transition"
            onError={(e) => {
              // Fallback to emoji on image load error
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <span 
            className="hidden h-12 w-12 items-center justify-center rounded-full bg-pink-500/10 border border-pink-400/40 text-lg"
          >
            {isGift ? (activity.gift_emoji || "🎁") : "💫"}
          </span>
          {/* Activity type indicator badge */}
          <span className={`absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full text-xs ${
            isGift ? 'bg-emerald-600 border border-emerald-400' : 'bg-pink-600 border border-pink-400'
          }`}>
            {isGift ? (activity.gift_emoji || "🎁") : "💫"}
          </span>
        </button>
      );
    }
    
    // Fallback to emoji icons if no photo
    if (isGift) {
      const giftEmoji = activity.gift_emoji || "🎁";
      return (
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-400/40 text-xl">
          {giftEmoji}
        </span>
      );
    }
    // Flirt
    return (
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-pink-500/10 border border-pink-400/40 text-lg">
        💫
      </span>
    );
  };

  const handleShowProfile = (e) => {
    e.stopPropagation();
    // Use the profile already extracted at top of component
    if (profile) {
      // Patch 1: Save scroll position before opening profile
      try {
        sessionStorage.setItem(
          FLIRTS_UI_STATE_KEY,
          JSON.stringify({
            scrollY: window.scrollY,
            at: Date.now(),
          })
        );
      } catch {
        // ignore
      }

      profileViewer.showProfile({
        display_name: profile.names?.join(' & ') || profile.display_name || name,
        age: profile.ages?.[0] || (profile.date_of_birth ? getAgeFromDob(profile.date_of_birth) : null),
        gender: profile.gender,
        relationship_style: profile.relationship_style,
        looking_for: profile.looking_for || [],
        location_city: profile.location_city,
        location_region: profile.location_region,
        bio: profile.bio,
        interests: profile.interests || [],
        // FIX #5: Deduplicate photos to prevent carousel glitch
        photos: deduplicatePhotos(profile.photos || profile.profile_photos || []),
        profile_photos: deduplicatePhotos(profile.profile_photos || profile.photos || []),
        // Include avatar and video for full media gallery
        avatar_url: profile.avatar_url || profile.main_photo_url,
        video_intro_url: profile.video_intro_url || profile.video_url,
        type: profile.type,
        user_type: profile.user_type || profile.type,
        background: profile.background,
        // Include verification and entitlements for badges
        identity_verified: profile.identity_verified || profile.is_verified,
        is_verified: profile.is_verified || profile.identity_verified,
        entitlements: profile.entitlements,
        is_inner_circle: profile.is_inner_circle,
      });
    }
  };

  const renderTitle = () => {
    if (isGift) {
      const giftName = activity.gift_name || "a gift";
      return (
        <>
          <button 
            onClick={handleShowProfile}
            className="font-semibold text-slate-50 hover:text-pink-300 transition"
          >
            {name}
          </button>{" "}
          <span className="text-slate-200">sent you {giftName}</span>
        </>
      );
    }
    return (
      <>
        <button 
          onClick={handleShowProfile}
          className="font-semibold text-slate-50 hover:text-pink-300 transition"
        >
          {name}
        </button>{" "}
        <span className="text-slate-200">{flirtLabel(activity.flirt_type)}</span>
      </>
    );
  };

  const renderChip = () => {
    if (isGift) {
      return (
        <span className="inline-flex items-center rounded-full bg-emerald-500/10 border border-emerald-500/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-emerald-200">
          <Gift className="w-3 h-3 mr-1" />
          Gift
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full bg-pink-500/10 border border-pink-500/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-pink-200">
        <Sparkles className="w-3 h-3 mr-1" />
        Flirt
      </span>
    );
  };

  const giftCost = isGift ? activity.gift_cost : undefined;

  return (
    <div className="group flex items-center justify-between rounded-2xl bg-slate-900/80 border border-slate-800/80 px-3 py-2.5 hover:border-pink-500/40 hover:bg-slate-900/90 transition">
      <div className="flex items-center gap-3">
        {renderLeftIcon()}
        <div className="space-y-0.5">
          <div className="text-sm leading-tight">{renderTitle()}</div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span>{createdAtLabel}</span>
            {giftCost != null && (
              <>
                <span className="h-1 w-1 rounded-full bg-slate-600" />
                <span>{giftCost} LoveCoins</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">{renderChip()}</div>
    </div>
  );
}

export default FlirtCenter;
